// client/src/pages/seo/RestaurantDigitalizationEn.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import SeoRelatedLinks from "../../components/seo/SeoRelatedLinks.jsx";
import SeoHeader from "../../components/seo/SeoHeader.jsx";
import SeoContactSection from "../../components/seo/SeoContactSection.jsx";
import SeoWorkSteps from "../../components/seo/SeoWorkSteps.jsx";

const sections = [
  {
    id: "check",
    label: "How digital is your restaurant already?",
  },
  {
    id: "definition",
    label: "What does restaurant digitalization mean?",
  },
  {
    id: "why",
    label: "Why restaurant digitalization matters now",
  },
  {
    id: "steps",
    label: "How to start restaurant digitalization step by step",
  },
  {
    id: "channels",
    label: "The most important digital building blocks for restaurants",
  },
  {
    id: "guest",
    label: "Think about digitalization from the guest’s perspective",
  },
  {
    id: "team",
    label: "Digitalization must also help the team",
  },
  {
    id: "platforms",
    label: "Why restaurants need digital independence",
  },
  {
    id: "mistakes",
    label: "Common restaurant digitalization mistakes",
  },
  {
    id: "bestplace",
    label: "How Best Place can help with restaurant digitalization",
  },
  {
    id: "faq",
    label: "FAQ about restaurant digitalization",
  },
];

const checklist = [
  "Your menu is hard to find online or not up to date.",
  "You want to connect QR code, menu, reservation, and website better.",
  "Reservations arrive through several channels in an unstructured way.",
  "You regularly update prices, photos, or availability.",
  "You want to depend less on paper, PDFs, or manual processes.",
  "Guests expect digital information before visiting.",
  "You want to simplify processes without replacing personal service.",
  "You want to become more digital step by step, without introducing a large system immediately.",
  "You want to depend less on platforms and social media only.",
];

const CALLBACK_API_ORIGIN =
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1")
    ? "http://localhost:3030"
    : "";

function getResult(score) {
  if (score <= 3) {
    return {
      title: "Digitalization may not be urgent yet",
      text:
        "If your key information is already current online and your daily processes work well, you can start small: menu, QR code, or a simple website.",
      tone: "low",
    };
  }

  if (score <= 6) {
    return {
      title: "Step-by-step digitalization makes sense",
      text:
        "Your restaurant can benefit from connecting individual digital building blocks: menu, QR code, website, reservation, and menu management.",
      tone: "medium",
    };
  }

  return {
    title: "Digitalization can become a central lever",
    text:
      "Several points suggest that your restaurant needs a clear digital strategy: better online visibility, a more current menu, less manual work, and easier paths for guests.",
    tone: "high",
  };
}

function VisualIdea({ children }) {
  return (
    <div className="seoVisualIdea" aria-label="Visual idea">
      <div className="seoVisualIdea__icon" aria-hidden="true">
        ✦
      </div>
      <div>
        <strong>Idea for illustration:</strong>
        <p>{children}</p>
      </div>
    </div>
  );
}

export default function RestaurantDigitalizationEn() {
  const [checked, setChecked] = useState([]);

  const score = checked.length;
  const result = useMemo(() => getResult(score), [score]);

  useEffect(() => {
    document.documentElement.setAttribute("lang", "en");
    document.title =
      "Restaurant Digitalization: Become Digital Step by Step | Best Place";
  }, []);

  const toggle = (item) => {
    setChecked((prev) =>
      prev.includes(item) ? prev.filter((x) => x !== item) : [...prev, item]
    );
  };

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  return (
    <main className="qrArticle">
      <SeoHeader
        lang="en"
        langLinks={{
          en: "/en/restaurant-digitalization",
          de: "/de/restaurant-digitalisierung",
          no: "/no/restaurant-digitalisering",
        }}
      />

      <section className="qrArticle__hero">
        <div className="qrArticle__heroInner">
          <h1 className="qrArticle__heroTitle">
            <span>
              Restaurant digitalization: how restaurants become digital step by
              step
            </span>
          </h1>

          <div className="qrArticle__topsection">
            <div className="qrArticle__topsection--img-wrapper">
              <div className="qrArticle__topsection--img"></div>
            </div>

            <div className="qrArticle__topsection--text-wrapper">
              <p className="seoHero__lead">
                Restaurant digitalization does not mean replacing personal
                service. It means using the right digital building blocks where
                they truly help guests and the team.
              </p>

              <p className="seoHero__text">
                For many restaurants, digitalization does not start with a large
                system. It starts with simple questions: Is our menu current
                online? Can guests reserve digitally? Does the QR code work? Can
                the team update prices, photos, and availability quickly?
              </p>
            </div>
          </div>
        </div>
      </section>

      <SeoContactSection lang="en" apiOrigin={CALLBACK_API_ORIGIN} />

      <section className="articls">
        <h2>Content of the article</h2>

        <nav className="qrArticle__nav" aria-label="Article navigation">
          <ol className="qrArticle__navList">
            {sections.map((item, index) => (
              <li key={item.id} className="qrArticle__navItem">
                <button
                  type="button"
                  className="qrArticle__navButton"
                  onClick={() => scrollToSection(item.id)}
                >
                  <span className="qrArticle__navNumber">
                    {String(index + 1).padStart(2, "0")}
                  </span>

                  <span className="qrArticle__navLabel">{item.label}</span>
                </button>
              </li>
            ))}
          </ol>
        </nav>
      </section>

      <article>
        <section id="check" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>How digital is your restaurant already?</h2>

            <p>
              Press the statements that sound true for your restaurant. The
              result is intentionally honest: not every restaurant needs a large
              digital system immediately, but many restaurants can benefit from
              connecting menu, QR code, website and reservation step by step.
            </p>

            <div className="qrArticle__checkGrid">
              {checklist.map((item) => (
                <label key={item} className="qrArticle__checkItem">
                  <input
                    type="checkbox"
                    checked={checked.includes(item)}
                    onChange={() => toggle(item)}
                  />
                  <span className="text">{item}</span>
                </label>
              ))}
            </div>

            <div className={`qrArticle__result qrArticle__result--${result.tone}`}>
              <div className="qrArticle__score">
                {score} / {checklist.length}
              </div>

              <div>
                <strong>{result.title}</strong>
                <p>{result.text}</p>
              </div>
            </div>
          </div>
        </section>

        <section id="definition" className="acquaintance">
          <h2>What does restaurant digitalization mean?</h2>

          <p>
            Restaurant digitalization means supporting analog or manual processes
            with digital tools where it makes sense: menu, QR code, reservation,
            ordering, payment, website, reviews, management, and internal
            workflows.
          </p>

          <p>
            It is not about automating every guest interaction. Good hospitality
            stays personal. Digitalization should reduce unnecessary friction:
            fewer outdated details, fewer lost requests, less duplicated manual
            work.
          </p>

          <ul className="qrArticle__block-li">
            <li className="text__seo-li">Digital menu.</li>
            <li className="text__seo-li">QR code access.</li>
            <li className="text__seo-li">Restaurant website.</li>
            <li className="text__seo-li">Online table reservation.</li>
            <li className="text__seo-li">Online menu management.</li>
            <li className="text__seo-li">Digital ordering workflows.</li>
            <li className="text__seo-li">Contactless payment.</li>
            <li className="text__seo-li">Structured administration.</li>
          </ul>

          <h3>Digital tools should reduce friction</h3>

          <p>
            The best digital tools do not make the restaurant feel colder or
            more complicated. They make information easier to find, changes
            easier to manage and the guest journey easier to understand.
          </p>
        </section>

        <section id="why" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Why restaurant digitalization matters now</h2>

            <p>
              Guests already use digital paths in everyday life: they search for
              restaurants online, check menus, reserve tables, and increasingly
              prefer contactless payment.
            </p>

            <p>
              A restaurant does not need to digitalize everything. But it should
              cover the most important touchpoints well: online visibility,
              current menu information, QR code access, reservation and mobile
              usability.
            </p>

            <h3>Start where guests already act digitally</h3>

            <p>
              The practical conclusion is simple: digitalization should begin
              where guests already use digital tools — search, menu, reservation
              and payment.
            </p>
          </div>
        </section>

        <section id="steps" className="acquaintance">
          <h2>How to start restaurant digitalization step by step</h2>

          <p>
            The most common mistake is treating digitalization as one big
            all-or-nothing project. For many restaurants, a step-by-step approach
            is better.
          </p>

          <ol className="seoSteps">
            <li className="text__seo-li">
              Make the most important information clear online.
            </li>
            <li className="text__seo-li">Create a digital menu.</li>
            <li className="text__seo-li">Use QR code access for guests.</li>
            <li className="text__seo-li">Make the menu editable online.</li>
            <li className="text__seo-li">Add reservation as the next step.</li>
            <li className="text__seo-li">
              Later add ordering or payment if it makes sense.
            </li>
            <li className="text__seo-li">
              Regularly check what guests actually use.
            </li>
          </ol>

          <h3>Digitalization is a system of useful improvements</h3>

          <p>
            It is not one button and not one giant launch. It is a sequence of
            small improvements that make the restaurant easier to find, easier
            to understand and easier to manage.
          </p>

          <VisualIdea>
            A step-by-step digitalization roadmap: website, digital menu, QR
            code, online reservation, menu management and later ordering or
            payment.
          </VisualIdea>
        </section>

        <section id="channels" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>The most important digital building blocks for restaurants</h2>

            <p>
              A restaurant does not need every technology immediately. The key
              is choosing the building blocks that fit the business model.
            </p>

            <div className="seoComparison">
              <div className="seoComparison__col">
                <h3>Core digital foundation</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">
                    <strong>Digital menu:</strong> Show the menu online and keep
                    prices and photos current.
                  </li>
                  <li className="text__seo-li">
                    <strong>QR code:</strong> Give guests fast access at the
                    table or in the window.
                  </li>
                  <li className="text__seo-li">
                    <strong>Website:</strong> Bring menu, contact, opening hours
                    and reservation together.
                  </li>
                </ul>
              </div>

              <div className="seoComparison__col seoComparison__col--highlight">
                <h3>Next digital steps</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">
                    <strong>Online reservation:</strong> Accept reservation
                    requests outside opening hours.
                  </li>
                  <li className="text__seo-li">
                    <strong>Ordering system:</strong> Make pickup, table
                    ordering or requests more structured.
                  </li>
                  <li className="text__seo-li">
                    <strong>Digital payment:</strong> Make payment faster and
                    more convenient.
                  </li>
                </ul>
              </div>
            </div>

            <div className="seoCompareSmall">
              <div>
                <strong>Related page</strong>
                <p>
                  <Link to="/en/digital-menu-for-restaurants">
                    Digital menu
                  </Link>
                </p>
              </div>

              <div>
                <strong>Related page</strong>
                <p>
                  <Link to="/en/online-table-reservation-for-restaurants">
                    Online table reservation
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </section>

        <section id="guest" className="acquaintance">
          <h2>Think about digitalization from the guest’s perspective</h2>

          <p>
            Guests do not think in software modules. They have simple questions:
            Where can I find the restaurant? What can I eat there? What does it
            cost? Can I reserve? Can I pay? Is the information current?
          </p>

          <ol className="seoSteps">
            <li className="text__seo-li">Find restaurant.</li>
            <li className="text__seo-li">View menu.</li>
            <li className="text__seo-li">Use QR code.</li>
            <li className="text__seo-li">Reserve table.</li>
            <li className="text__seo-li">Plan visit.</li>
          </ol>

          <p>
            The shorter and clearer this path is, the less likely the guest is
            to lose orientation on the way.
          </p>
        </section>

        <section id="team" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Digitalization must also help the team</h2>

            <p>
              A digital system is only good if it does not just look good for
              guests, but also works in the team’s daily routine.
            </p>

            <div className="seoComparison">
              <div className="seoComparison__col">
                <h3>Bad digitalization</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">Too many separate tools.</li>
                  <li className="text__seo-li">Complicated usage.</li>
                  <li className="text__seo-li">Unclear responsibilities.</li>
                  <li className="text__seo-li">
                    More work instead of less work.
                  </li>
                </ul>
              </div>

              <div className="seoComparison__col seoComparison__col--highlight">
                <h3>Good digitalization</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">Clear workflows.</li>
                  <li className="text__seo-li">Easy management.</li>
                  <li className="text__seo-li">Current information.</li>
                  <li className="text__seo-li">Less duplicated work.</li>
                  <li className="text__seo-li">More time for guests.</li>
                </ul>
              </div>
            </div>

            <p>
              A digital menu that nobody updates is not useful. A reservation
              feature that the team sees too late creates new problems.
              Digitalization must fit the real workflow.
            </p>
          </div>
        </section>

        <section id="platforms" className="acquaintance">
          <h2>Why restaurants need digital independence</h2>

          <p>
            Platforms, social media, and search engines are important. They can
            bring reach. Still, a restaurant should not depend completely on
            external channels.
          </p>

          <p>
            A restaurant’s own digital foundation — website, menu, QR code,
            reservation — gives more control over content, presentation, and the
            guest journey.
          </p>

          <div className="seoComparison">
            <div className="seoComparison__col">
              <h3>External channels only</h3>

              <ul className="qrArticle__block-li">
                <li className="text__seo-li">
                  Less control over presentation.
                </li>
                <li className="text__seo-li">
                  Information is spread across platforms.
                </li>
                <li className="text__seo-li">
                  Layout and rules can change.
                </li>
                <li className="text__seo-li">
                  Menu, QR code and reservation may stay disconnected.
                </li>
              </ul>
            </div>

            <div className="seoComparison__col seoComparison__col--highlight">
              <h3>Own digital foundation</h3>

              <ul className="qrArticle__block-li">
                <li className="text__seo-li">Central restaurant page.</li>
                <li className="text__seo-li">Own digital menu.</li>
                <li className="text__seo-li">QR code destination.</li>
                <li className="text__seo-li">Reservation path.</li>
                <li className="text__seo-li">
                  More control over the guest journey.
                </li>
              </ul>
            </div>
          </div>
        </section>

        <section id="mistakes" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Common restaurant digitalization mistakes</h2>

            <p>
              Digitalization should make daily work clearer, not heavier. Many
              problems appear when a restaurant starts with too many tools or no
              clear goal.
            </p>

            <div className="seoComparison">
              <div className="seoComparison__col">
                <h3>Common mistakes</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">
                    Too many tools are introduced at once.
                  </li>
                  <li className="text__seo-li">
                    The digital change has no clear goal.
                  </li>
                  <li className="text__seo-li">
                    The guest journey is not tested on a smartphone.
                  </li>
                  <li className="text__seo-li">
                    The team is not involved in the workflow.
                  </li>
                  <li className="text__seo-li">
                    Old prices, opening hours or photos stay online.
                  </li>
                  <li className="text__seo-li">
                    The restaurant depends only on external platforms.
                  </li>
                </ul>
              </div>

              <div className="seoComparison__col seoComparison__col--highlight">
                <h3>Better</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">
                    Start with one useful digital step.
                  </li>
                  <li className="text__seo-li">
                    Define which problem the tool solves.
                  </li>
                  <li className="text__seo-li">
                    Test the flow on real smartphones.
                  </li>
                  <li className="text__seo-li">
                    Include the team early.
                  </li>
                  <li className="text__seo-li">
                    Keep menu and restaurant information current.
                  </li>
                  <li className="text__seo-li">
                    Build your own digital foundation.
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <div id="bestplace" className="qrArticle__anchorTarget">
          <SeoWorkSteps lang="en" />
        </div>

        <section id="faq" className="acquaintance">
          <h2>Frequently asked questions about restaurant digitalization</h2>

          <div className="seoFaq">
            <details>
              <summary>
                Does a restaurant need to digitalize everything immediately?
              </summary>
              <p>
                No. A step-by-step approach is often better: digital menu, QR
                code, website, reservation, and then additional features.
              </p>
            </details>

            <details>
              <summary>Does digitalization replace personal service?</summary>
              <p>
                No. Good digitalization supports service by making information,
                reservations, and menu management easier.
              </p>
            </details>

            <details>
              <summary>What should be digitalized first?</summary>
              <p>
                The menu is often the best starting point because guests want to
                see it before visiting and before ordering.
              </p>
            </details>

            <details>
              <summary>Is digitalization useful for small restaurants?</summary>
              <p>
                Yes, especially when small businesses want to manage changes
                themselves and depend less on print, PDFs, or external help.
              </p>
            </details>

            <details>
              <summary>What role does QR code play?</summary>
              <p>
                QR code can be the fast entry point: guests scan the code and
                open the menu, restaurant page, or reservation flow.
              </p>
            </details>
          </div>
        </section>
        <SeoRelatedLinks
          lang="en"
          group="business"
          exclude="/en/restaurant-digitalization"
        />
      </article>
    </main>
  );
}